'use client';

import { Slider as SliderPrimitive, type SliderRoot } from '@base-ui-components/react/slider';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * Generic in its value, so a caller that names its shape is answered in that
 * shape: `Slider<readonly [number, number]>` reports a pair back and a plain
 * number slider reports a number. Left to the constraint, every consumer would
 * have to re-narrow a `number | readonly number[]` it already knew the shape
 * of, and would have nowhere honest to put the impossible branch.
 */
export type SliderProps<Value extends number | readonly number[] = number | readonly number[]> =
  SliderRoot.Props<Value> & {
    /**
     * The accessible name of the thumb at `index`.
     *
     * The thumbs are anatomy this component renders rather than children a
     * caller composes, so a caller cannot reach them to label them — and a
     * range slider with two identically-named handles is unusable to a screen
     * reader. Base UI forwards the returned string to each thumb's nested
     * `<input type="range">`, which is the element the assistive technology
     * actually reads.
     */
    readonly getThumbAriaLabel?: (index: number) => string;
  };

// Range support: Base UI renders one Thumb per entry of the value array
// (`<Slider.Thumb index={n} />`), so the thumb count is derived from the
// controlled/uncontrolled value shape — `[min, max]` yields the two-thumb
// price-range slider, a plain number the classic single-thumb one.
export const Slider = <Value extends number | readonly number[]>({
  className,
  value,
  defaultValue,
  children,
  getThumbAriaLabel,
  ...props
}: SliderProps<Value>) => {
  const current = value ?? defaultValue;
  const thumbCount = Array.isArray(current) ? current.length : 1;

  return (
    <SliderPrimitive.Root<Value>
      data-slot="slider"
      className={clsxm('w-full', className)}
      {...(value === undefined ? {} : { value })}
      {...(defaultValue === undefined ? {} : { defaultValue })}
      {...props}
    >
      {children}
      <SliderPrimitive.Control
        data-slot="slider-control"
        className="flex h-8 w-full touch-none items-center select-none"
      >
        <SliderPrimitive.Track
          data-slot="slider-track"
          className="bg-background h-1 w-full rounded-full select-none"
        >
          {/* The indicator inherits the track's height and takes its width from
              the value, so the track is what decides how thick the bar reads. */}
          <SliderPrimitive.Indicator
            data-slot="slider-indicator"
            className="bg-primary select-none"
          />
          {Array.from({ length: thumbCount }, (_, index) => (
            <SliderPrimitive.Thumb
              key={index}
              index={index}
              data-slot="slider-thumb"
              {...(getThumbAriaLabel === undefined ? {} : { getAriaLabel: getThumbAriaLabel })}
              className={clsxm(
                'bg-primary ring-primary/20 size-3 rounded-full ring-6 outline-none select-none',
                // Two things happen on keyboard focus, and neither moves the
                // thumb: the halo deepens, and an outline is drawn inside it.
                // An outline rather than a wider ring because the halo IS the
                // thumb's visual weight — growing it makes the thumb appear to
                // jump the moment it is tabbed to — and an outline is painted
                // outside the border box without taking any room.
                'focus-visible:ring-primary/50',
                'focus-visible:outline-foreground focus-visible:outline-2 focus-visible:outline-offset-2',
                'data-disabled:cursor-not-allowed data-disabled:opacity-50'
              )}
            />
          ))}
        </SliderPrimitive.Track>
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  );
};

// Formatted current value(s), e.g. the "120 — 890 Kč" readout of the
// price-range filter. Plain passthrough so callers control placement.
export const SliderValue = ({
  className,
  ...props
}: ComponentProps<typeof SliderPrimitive.Value>) => (
  <SliderPrimitive.Value
    data-slot="slider-value"
    className={clsxm('text-foreground-muted text-sm', className)}
    {...props}
  />
);
